/* Heading-rule enhancement. Source nodes remain the sole copy of the content. */
(() => {
  'use strict';
  const aliases = {
    overview: ['overview', 'description', 'product description', 'product overview', 'aperçu', 'description du produit'],
    features: ['key features', 'features', 'product features', 'feature visual guide', 'features and benefits', 'features & benefits', 'caractéristiques', 'caractéristiques principales'],
    specs: ['specifications', 'specs', 'technical specifications', 'technical details', 'dimensions', 'dimensions & weight', 'dimensions and weight', 'product dimensions', "what's included", 'what’s included', 'inside the box', "what's in the box", 'spécifications', 'dimensions et poids', 'contenu de la boîte'],
    faqs: ['faq', 'faqs', 'frequently asked questions', 'questions fréquentes', 'foire aux questions'],
    care_manual: ['care & use', 'care and use', 'care instructions', 'care notes', 'care', 'washing instructions', 'cleaning instructions', "owner's manual", 'owner’s manual', 'user manual', 'instruction manual', 'manuals', 'entretien', "conseils d'entretien", 'manuel'],
    size_fit: ['size & fit', 'size and fit', 'size guide', 'sizing guide', 'size chart', 'fit guide', 'guide des tailles'],
    materials: ['materials', 'materials & care', 'materials and care', 'fabric & care', 'fabric and care', 'fabric', 'composition', 'matériaux', 'matières et entretien'],
    ingredients: ['ingredients', 'ingredient list', 'full ingredient list', 'key ingredients', 'active ingredients', 'inactive ingredients', 'ingrédients', 'liste des ingrédients', 'ingrédients actifs', 'ingrédients inactifs']
  };
  const order = ['overview', 'features', 'specs', 'size_fit', 'materials', 'ingredients', 'faqs', 'care_manual', 'more_info', 'price_match'];
  const normalize = value => value.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim().replace(/[:：]\s*$/, '').toLocaleLowerCase();
  const labelText = value => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    return decoder.value;
  };
  const lookup = new Map(Object.entries(aliases).flatMap(([key, words]) => words.map(word => [normalize(word), key])));
  const meaningful = node => node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim());
  const isHeading = node => node?.nodeType === 1 && /^H[2-6]$/.test(node.tagName);
  const blockHeading = node => {
    if (node.nodeType !== 1 || !['DIV', 'SECTION', 'ARTICLE'].includes(node.tagName)) return null;
    const first = Array.from(node.childNodes).find(meaningful);
    return isHeading(first) ? first : null;
  };
  const hasContent = nodes => nodes.some(node => node.textContent.trim() || (node.nodeType === 1 &&
    (node.matches('img,video,audio,iframe,table,svg,hr') || node.querySelector('img,video,audio,iframe,table,svg,hr'))));
  const nunaFaqStyle = '.nuna-pdp-v190 summary::-webkit-details-marker{display:none}.nuna-pdp-v190 .faq-open{display:none}.nuna-pdp-v190 details[open]>summary .faq-closed{display:none}.nuna-pdp-v190 details[open]>summary .faq-open{display:inline}';
  const supportedStyle = node => node.parentElement?.classList.contains('nuna-pdp-v190') &&
    node.textContent.trim() === nunaFaqStyle;

  function plan(source, enabled = true) {
    const fallback = reason => ({container: source, groups: new Map([['overview', Array.from(source.childNodes)]]), split: false, reason});
    if (!enabled) return fallback('disabled-or-ineligible');
    if (source.querySelector('script,form,input,select,textarea,button,iframe,[role="tablist"],[data-lw-tabs]') ||
      Array.from(source.querySelectorAll('style')).some(node => !supportedStyle(node))) return fallback('embedded-interaction');
    let container = source;
    let sectionBlocks = false;
    for (let depth = 0; depth < 6; depth++) {
      if (/display\s*:\s*(?:flex|grid)/i.test(container.getAttribute('style') || '')) return fallback('layout-container');
      const children = Array.from(container.childNodes).filter(meaningful);
      if (children.some(isHeading)) break;
      if (children.length > 1 && children.some(node => lookup.has(normalize(blockHeading(node)?.textContent || '')))) {
        sectionBlocks = true;
        break;
      }
      if (children.length !== 1 || children[0].nodeType !== 1 ||
        !['DIV', 'SECTION', 'ARTICLE'].includes(children[0].tagName)) return fallback('no-safe-heading-boundary');
      container = children[0];
    }
    if (/display\s*:\s*(?:flex|grid)/i.test(container.getAttribute('style') || '')) return fallback('layout-container');
    const nodes = Array.from(container.childNodes);
    const headings = sectionBlocks ? nodes.map(blockHeading).filter(Boolean) : nodes.filter(isHeading);
    if (sectionBlocks && nodes.some(node => {
      const heading = blockHeading(node);
      return heading && lookup.has(normalize(heading.textContent)) &&
        Array.from(node.children).some(child => child !== heading && isHeading(child) &&
          Number(child.tagName.slice(1)) <= Number(heading.tagName.slice(1)));
    })) return fallback('ambiguous-section-block');
    // Only peer section headings may split content. A subordinate heading such
    // as "Dimensions" inside Features must stay with its parent section.
    const recognized = headings.filter(node => lookup.has(normalize(node.textContent)));
    const sectionLevel = Math.min(...recognized.map(node => Number(node.tagName.slice(1))));
    const isBoundary = node => isHeading(node) && (sectionBlocks || Number(node.tagName.slice(1)) <= sectionLevel);
    const visualGuide = headings.some(node => isBoundary(node) && normalize(node.textContent) === 'feature visual guide');
    const category = heading => visualGuide && normalize(heading.textContent) === 'key features' ? 'overview' : lookup.get(normalize(heading.textContent));
    if (!headings.some(heading => category(heading) && category(heading) !== 'overview')) return fallback('no-recognized-sections');
    // References spanning different panels (including SVG use/ARIA) need manual review.
    if (source.querySelector('[aria-controls],[aria-labelledby],[aria-describedby],a[href^="#"],use')) return fallback('cross-section-reference');
    const groups = new Map();
    let key = 'overview';
    let unknown = 0;
    for (const node of nodes) {
      if (sectionBlocks) {
        const heading = blockHeading(node);
        key = heading ? category(heading) || 'overview' : 'overview';
        if (heading && !category(heading)) unknown++;
      } else if (isBoundary(node)) {
        key = category(node) || 'overview';
        if (!category(node)) unknown++;
      }
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(node);
    }
    const ingredientContent = (groups.get('ingredients') || []).flatMap(node => {
      const heading = sectionBlocks && blockHeading(node);
      return heading ? Array.from(node.childNodes).filter(child => child !== heading) : isBoundary(node) ? [] : [node];
    });
    if (groups.has('ingredients') && !hasContent(ingredientContent)) {
      const overviewNodes = new Set([...(groups.get('overview') || []), ...groups.get('ingredients')]);
      groups.set('overview', nodes.filter(node => overviewNodes.has(node)));
      groups.delete('ingredients');
    }
    const flattened = Array.from(groups.values()).flat();
    if (flattened.length !== nodes.length || new Set(flattened).size !== nodes.length ||
      nodes.some(node => !flattened.includes(node))) return fallback('integrity-check');
    return {container, groups, split: true, sectionBlocks, reason: sectionBlocks ?
      (unknown ? 'partial-section-blocks' : 'recognized-section-blocks') :
      (unknown ? 'partial-unrecognized-in-overview' : 'recognized')};
  }

  function init(root) {
    if (root.dataset.descriptionReady) return;
    const source = root.querySelector('[data-lw-description-source]');
    if (!source) return;
    const decision = plan(source, root.dataset.autoSplit === 'true');
    const labels = JSON.parse(root.querySelector('[data-lw-description-labels]').textContent);
    const groups = decision.groups;
    const extras = Array.from(root.querySelectorAll('[data-lw-description-extra]'));
    const extraLabels = {};
    extras.forEach(extra => {
      const key = extra.dataset.lwDescriptionExtra;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(extra);
      extraLabels[key] = extra.dataset.label;
    });
    const visibleKeys = order.filter(key => groups.has(key) && hasContent(groups.get(key)));
    if (!visibleKeys.length) return;
    const originalNodes = Array.from(decision.container.childNodes);
    const sourceElements = Array.from(source.querySelectorAll('*'));
    const sourceTextNodes = [];
    const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) sourceTextNodes.push(walker.currentNode);
    const nav = document.createElement('div');
    nav.className = 'lw-description-tabs';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', root.querySelector('header h2').textContent);
    const panels = [], tabs = [];
    let active = 0;
    visibleKeys.forEach((key, index) => {
      const id = root.id + '-' + key;
      const label = extraLabels[key] || (key === 'overview' && !decision.split ? labels.description : labels[key]);
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = id + '-tab';
      tab.textContent = labelText(label);
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      nav.append(tab);
      tabs.push(tab);
      const panel = document.createElement('div');
      panel.id = id;
      panel.className = 'lw-description-panel';
      panel.append(...groups.get(key));
      panels.push(panel);
      decision.container.append(panel);
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', event => {
        const direction = getComputedStyle(nav).direction === 'rtl' ? -1 : 1;
        let target = index;
        if (event.key === 'ArrowRight') target = (index + direction + tabs.length) % tabs.length;
        else if (event.key === 'ArrowLeft') target = (index - direction + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;
        event.preventDefault();
        tabs[target].focus({preventScroll: true});
        activate(target);
      });
    });
    // Post-move guard checks exact original node identities, not just text lengths.
    if (sourceElements.some(node => !source.contains(node)) || sourceTextNodes.some(node => !source.contains(node))) {
      decision.container.replaceChildren(...originalNodes);
      extras.forEach(extra => root.append(extra));
      root.dataset.descriptionFallback = 'integrity-check';
      return;
    }
    source.before(nav);
    function render() {
      panels.forEach((panel, index) => {
        const selected = active === index;
        panel.hidden = !selected;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabs[index].id);
        panel.tabIndex = 0;
        tabs[index].setAttribute('aria-selected', String(selected));
        tabs[index].tabIndex = selected ? 0 : -1;
        if (!selected) panel.querySelectorAll('video').forEach(video => video.pause());
      });
    }
    function revealTab() {
      const rect = nav.getBoundingClientRect(), selected = tabs[active].getBoundingClientRect();
      const delta = selected.left < rect.left + 10 ? selected.left - rect.left - 10 :
        selected.right > rect.right - 10 ? selected.right - rect.right + 10 : 0;
      if (delta) nav.scrollBy({left: delta, behavior: 'instant'});
    }
    function activate(index) {
      const top = nav.getBoundingClientRect().top;
      active = index;
      render();
      revealTab();
      if (top >= 0 && top < innerHeight) {
        const delta = nav.getBoundingClientRect().top - top;
        if (Math.abs(delta) > 1) window.scrollBy({top: delta, behavior: 'instant'});
      }
    }
    function onHash() {
      let hash = location.hash.slice(1);
      try { hash = decodeURIComponent(hash); } catch { return; }
      // Keep already-shared pilot links working.
      const legacyKey = hash.startsWith('callisto-') ? hash.slice(9).replace('care-manual', 'care_manual').replace('price-match', 'price_match').replace('specifications', 'specs') : null;
      const index = panels.findIndex((panel, i) => panel.id === hash || visibleKeys[i] === legacyKey ||
        Array.from(panel.querySelectorAll('[id]')).some(node => node.id === hash));
      if (index < 0) return;
      active = index; render(); revealTab();
      requestAnimationFrame(() => (document.getElementById(hash) || panels[index]).scrollIntoView({block: 'start'}));
    }
    root.dataset.descriptionReady = 'true';
    root.dataset.descriptionMode = decision.reason;
    source.classList.add('lw-description-source--enhanced');
    render(); onHash();
    root.dispatchEvent(new Event('lw:description-ready'));
    window.addEventListener('resize', revealTab);
    window.addEventListener('hashchange', onHash);
    root.descriptionCleanup = () => {
      window.removeEventListener('resize', revealTab);
      window.removeEventListener('hashchange', onHash);
    };
  }
  window.LWDescriptionTabs = {plan, init};
  const scan = target => target.querySelectorAll('[data-lw-description]').forEach(init);
  scan(document);
  if (!window.lwDescriptionEvents) {
    window.lwDescriptionEvents = true;
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event =>
      event.target.querySelectorAll('[data-lw-description]').forEach(root => root.descriptionCleanup?.()));
  }
})();
